import { CalendarContext } from '../../../../../utils/context/contextFactory';
import { CalendarState } from '../../../controller';
import { getPaymentDescription } from '../../../../../utils/payment/payment';
import {
  isServiceOfferedOnline,
  isCustomerCannotChooseStaff,
  isStaffVariant,
  getFormattedCurrency,
  getServiceCurrency,
} from '@wix/bookings-calendar-catalog-viewer-mapper';
import { getVideoConferencingBadge } from '../bookingDetailsViewModel';
import { BookingPreference } from '../../../../../utils/bookingPreferences/bookingPreferences';
import { Preference } from '../../../../../types/types';
import { DO_NOT_CARE_STAFF } from '../../../../../constants/constants';
import { StaffMember } from '@wix/ambassador-bookings-services-v2-service/types';

export type SelectedSlotDetailsViewModel = {
  paymentDescription?: string;
  videoConferenceBadgeText?: string;
  locationLabel?: string;
  staffMemberLabel?: string;
  durationLabel?: string;
  addOnsLabel?: string;
  dateAndTime?: string;
};

export type CalendarSummaryViewModel = {
  serviceName: string;
  openPreferencesModalButton?: {
    text: string;
    ariaLabel: string;
  };
  selectedSlotDetails: SelectedSlotDetailsViewModel;
};

export const createCalendarSummaryViewModel = ({
  context,
  state,
  baseBookingPreferences,
  dateAndTime,
}: {
  context: CalendarContext;
  state: CalendarState;
  baseBookingPreferences: BookingPreference[];
  dateAndTime?: string;
}): CalendarSummaryViewModel => {
  const { t, calendarSelections, bookingsSettings } = context;
  const {
    servicesInView,
    selectedVariantsOptions,
    rescheduleBookingDetails,
    isPreferencesModalPartOfTheFlow,
    serviceVariantsMap,
  } = state;
  const service = servicesInView[0];
  const serviceName = service.name!;

  const videoConferenceBadgeText = isServiceOfferedOnline(service)
    ? getVideoConferencingBadge(context)
    : '';

  const preSelectedPreferences = calendarSelections?.services?.[0];

  const getPreferenceValueFromDropdownIfOnlyOneOption = (
    preferenceKey: Preference,
  ): string | undefined => {
    const preference = baseBookingPreferences?.find(
      (pref) => pref.key === preferenceKey,
    );
    return preference?.options.length === 1
      ? preference.options[0].value
      : undefined;
  };

  const getLocationFromCalendarSelections = (): string | undefined => {
    const locationId = calendarSelections?.location;
    if (!locationId) {
      return;
    }
    return service?.locations?.find((l) => l.id === locationId)?.business?.name;
  };

  const getStaffMemberFromCalendarSelections = (): string | undefined => {
    const staffIds = preSelectedPreferences?.resources;
    if (staffIds?.length !== 1) {
      return;
    }
    const staffId = staffIds[0];
    if (staffId === DO_NOT_CARE_STAFF) {
      return t('services-preferences-modal.summary.select-any-staff');
    }

    const staffName = service?.staffMembers?.find(
      (staff: StaffMember) => staff.staffMemberId === staffId,
    )?.name;
    if (staffName) {
      return staffName;
    }
  };

  const locationLabel =
    getLocationFromCalendarSelections() ??
    getPreferenceValueFromDropdownIfOnlyOneOption(Preference.LOCATION);

  const shouldHideStaffMemberLabel =
    isCustomerCannotChooseStaff(bookingsSettings) &&
    !isStaffVariant(serviceVariantsMap[service.id!]);

  const staffMemberLabel = shouldHideStaffMemberLabel
    ? undefined
    : getStaffMemberFromCalendarSelections() ??
      getPreferenceValueFromDropdownIfOnlyOneOption(Preference.STAFF_MEMBER);

  const durationLabel = getPreferenceValueFromDropdownIfOnlyOneOption(
    Preference.DURATION,
  );

  const paymentDescription = !rescheduleBookingDetails
    ? getPaymentDescription({
        service,
        selectedVariantsOptions,
        context,
      })
    : undefined;
  const addOnsLabel = getAddOnsSummary({
    context,
    state,
  });

  return {
    serviceName,
    openPreferencesModalButton: isPreferencesModalPartOfTheFlow
      ? {
          text: t('app.summary.open-preferences-modal-button.text'),
          ariaLabel: t('app.summary.open-preferences-modal-button.aria-label'),
        }
      : undefined,
    selectedSlotDetails: {
      dateAndTime,
      paymentDescription,
      videoConferenceBadgeText,
      locationLabel,
      staffMemberLabel,
      durationLabel,
      addOnsLabel,
    },
  };
};

const getAddOnsSummary = ({
  context,
  state,
}: {
  context: CalendarContext;
  state: CalendarState;
}): string | undefined => {
  const { calendarSelections, t, languageSettings } = context;
  const service = state.servicesInView[0];

  const selectedFromA1 = calendarSelections?.services?.[0]?.addOns;
  const selectedFromReschedule = state.rescheduleBookingDetails?.bookedAddOns;
  const selected = selectedFromA1 || selectedFromReschedule;

  if (!selected?.length) {
    return;
  } else if (selectedFromReschedule?.length) {
    return t('app.summary.addons-count', {
      count: selectedFromReschedule.length,
    });
  }

  let totalPrice = 0;
  for (const item of selectedFromA1!) {
    const quantity = item.quantity;
    if (item.price) {
      totalPrice += item.price * quantity;
    }
  }
  const addOnsCount = selectedFromA1?.length!;
  const label = t('app.summary.addons-count', { count: addOnsCount });
  if (totalPrice > 0) {
    const currency = getServiceCurrency(service);
    if (!currency) {
      return label;
    }
    const formattedPrice = getFormattedCurrency({
      price: totalPrice,
      currency,
      locale: languageSettings.regionalSettingsLocale!,
    });
    return `${label} (${formattedPrice})`;
  }

  return label;
};
